import { useState } from 'react'
import { BulkProgressBar } from 'app/components/table/bulkActions/BulkProgressBar'
import { BulkResultsSummary } from 'app/components/table/bulkActions/BulkResultsSummary'
import type { BulkProgress, BulkProgressReporter, BulkRowOutcome } from 'app/components/table/bulkActions/types'
import { Button } from 'app/components/ui/button'
import { DialogActionBar } from 'app/components/ui/dialog'
import { Label } from 'app/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import type { ListStage } from 'app/generated/graphql'
import { closeModal } from 'app/lib/modal'

/** Asks where a stage's people go before the stage is deleted, then moves them and reports who could not be moved. */
export function MoveStageMembersDialog({
  stageName,
  count,
  targets,
  initialTargetId,
  onMove,
}: {
  stageName: string
  count: number
  targets: readonly ListStage[]
  initialTargetId: string | undefined
  /** Resolves with the outcomes only when someone could not be moved, which keeps the stage. */
  onMove: (targetId: string, onProgress: BulkProgressReporter) => Promise<BulkRowOutcome[] | undefined>
}) {
  const [targetId, setTargetId] = useState(initialTargetId)
  const [progress, setProgress] = useState<BulkProgress>()
  const [outcomes, setOutcomes] = useState<BulkRowOutcome[]>()
  const running = !!progress && !outcomes
  const people = count === 1 ? `person is` : `people are`

  const move = async () => {
    if (!targetId) return
    setProgress({ done: 0, total: count, ok: 0, failed: 0, skipped: 0 })
    const kept = await onMove(targetId, setProgress)
    if (kept) setOutcomes(kept)
    else setProgress(undefined)
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-4 px-6 py-5">
        {outcomes ? (
          <>
            <p className="rounded-md bg-warning-minimal px-3 py-2 text-ui-sm text-warning-strong">
              {stageName} was kept, because not everyone could be moved. Delete it again to move the rest.
            </p>
            <BulkResultsSummary outcomes={outcomes} successWord="moved" />
          </>
        ) : (
          <>
            <p className="text-ui-sm text-default">
              {count} {people} in <span className="font-semibold text-strong">{stageName}</span>. Choose where they go
              before it is deleted.
            </p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="move-stage-target">Move them to</Label>
              <Select value={targetId} onValueChange={setTargetId} disabled={running}>
                <SelectTrigger id="move-stage-target">
                  <SelectValue placeholder="Choose a stage" />
                </SelectTrigger>
                <SelectContent>
                  {targets.map((stage) => (
                    <SelectItem key={stage.id} value={stage.id}>
                      {stage.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {progress && <BulkProgressBar progress={progress} verb="Moving" />}
          </>
        )}
      </div>
      <DialogActionBar>
        {outcomes ? (
          <Button variant="primary" size="md" onClick={closeModal}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" size="md" onClick={closeModal} disabled={running}>
              Cancel
            </Button>
            <Button variant="primary" size="md" onClick={move} disabled={running || !targetId} loading={running}>
              Move and delete
            </Button>
          </>
        )}
      </DialogActionBar>
    </div>
  )
}
